import { useEffect, useState, useCallback } from 'react';
import { AlertCircle, Ban, RefreshCw, Search, Info, ShieldCheck } from 'lucide-react';
import {
  panelBanneds, guardUnban,
  type BannedView,
} from '@/shared/services/adminService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';
import { GuardDetailModal, GuardBanModal } from './GuardModals';

export default function BannedsPage() {
  const { hasFeature } = useAuth();
  const { notify } = useToast();
  const canBan = hasFeature('ban');

  const [items, setItems] = useState<BannedView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const [detailUserNum, setDetailUserNum] = useState<number | null>(null);
  const [banTarget, setBanTarget] = useState<{ userNum: number; accountId?: string | null } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await panelBanneds());
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUnban = async (userNum: number) => {
    try {
      await guardUnban(userNum);
      notify('success', 'Desbanido');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const cols: DataTableColumn<BannedView>[] = [
    {
      key: 'id', label: 'ID da Conta', width: '1fr',
      cell: (b) => <span className="text-sm font-bold text-white truncate">{b.id || '—'}</span>,
    },
    {
      key: 'releasedate', label: 'Liberar em', width: '200px',
      cell: (b) => (
        <span className="text-xs text-white/55 font-mono">
          {b.releasedate ? new Date(b.releasedate).toLocaleString('pt-BR') : '—'}
        </span>
      ),
    },
    {
      key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
      cell: (b) => b.userNum != null ? (
        <RowActions
          actions={[
            {
              label: 'Detalhes (HWID + histórico)',
              icon: <Info size={13} />,
              onClick: () => setDetailUserNum(b.userNum!),
              variant: 'primary',
            },
            ...(canBan ? [{
              label: 'Desbanir',
              icon: <ShieldCheck size={13} />,
              onClick: () => handleUnban(b.userNum!),
              variant: 'success' as const,
            }] : []),
          ]}
        />
      ) : null,
    },
  ];

  const filtered = filter.trim()
    ? items.filter((b) => (b.id || '').toLowerCase().includes(filter.toLowerCase()))
    : items;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Ban className="text-red-400" size={26} />
            Contas Banidas
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {items.length.toLocaleString('pt-BR')} bans ativos · clique numa linha pra abrir a conta
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-amber-500/10 border border-amber-500/30 text-amber-400
                     hover:bg-amber-500/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      <div className="mb-5 relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrar por ID..."
          className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg pl-11 pr-4 py-2.5
                     text-sm text-white placeholder:text-white/40
                     focus:outline-none focus:border-amber-500/40 transition"
        />
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<BannedView>
        columns={cols}
        rows={filtered}
        rowKey={(b) => `${b.id}-${b.releasedate ?? ''}`}
        rowHref={(b) => b.userNum != null ? `/admin/account/${b.userNum}` : ''}
        loading={loading}
        empty={filter ? 'Nenhum resultado pro filtro' : 'Nenhuma conta banida'}
        mobileTitle={(b) => (b.id || '—')}
        mobileSubtitle={(b) => b.releasedate ? `Liberar ${new Date(b.releasedate).toLocaleString('pt-BR')}` : ''}
        mobileActions={(b) => b.userNum != null ? (
          <RowActions
            actions={[
              {
                label: 'Detalhes (HWID + histórico)',
                icon: <Info size={13} />,
                onClick: () => setDetailUserNum(b.userNum!),
                variant: 'primary',
              },
              ...(canBan ? [{
                label: 'Desbanir',
                icon: <ShieldCheck size={13} />,
                onClick: () => handleUnban(b.userNum!),
                variant: 'success' as const,
              }] : []),
            ]}
          />
        ) : null}
      />

      {detailUserNum != null && (
        <GuardDetailModal
          userNum={detailUserNum}
          canBan={canBan}
          onClose={() => setDetailUserNum(null)}
          onBan={(userNum, accountId) => {
            setDetailUserNum(null);
            setBanTarget({ userNum, accountId });
          }}
          onChanged={fetchData}
        />
      )}

      {banTarget && (
        <GuardBanModal
          target={banTarget}
          onClose={() => setBanTarget(null)}
          onDone={() => {
            setBanTarget(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
